import React, { useRef } from 'react'
import { toast } from 'react-toastify'
import swal from 'sweetalert2'
import PropTypes from 'prop-types'

import { useSetState } from '@campaignhub/react-hooks'

import {
  Box, Button, Checkbox, DashboardModule, Text,
} from '@campaignhub/suit-theme'

import useServiceJobChecklistItem from '@hooks/useServiceJobChecklistItem'
import MutationStatus from '../MutationStatus'

const defaultState = { newNoteContent: '', editingId: null, editingContent: '' }

const ChecklistItems = (props) => {
  const {
    addPadding = '8px 16px', checkable, dashboardModule, editable, hideItemBorderBottom, itemPadding = '12px 16px', serviceJobId, serviceJobChecklistItemTypeId, showUpdatedStatus, title,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { newNoteContent, editingId, editingContent } = state

  const addInputRef = useRef(null)

  const {
    callbacks: {
      createServiceJobChecklistItem: createFn,
      updateServiceJobChecklistItem: updateFn,
      deleteServiceJobChecklistItem: deleteFn,
    },
    creating,
    deleting,
    updating,
    serviceJobChecklistItems,
  } = useServiceJobChecklistItem()

  const filteredNotes = Object.values(serviceJobChecklistItems || {}).filter(
    x => x.serviceJobCheckListItemTypeId === serviceJobChecklistItemTypeId && x.serviceJobId === serviceJobId,
  )

  const handleAdd = () => {
    if (!newNoteContent.trim()) return
    createFn({ description: newNoteContent.trim(), serviceJobId, serviceJobCheckListItemTypeId: serviceJobChecklistItemTypeId }).then(({ success, errors }) => {
      if (success) {
        setState({ newNoteContent: '' })
        if (addInputRef.current) addInputRef.current.style.height = 'auto'
      } else {
        toast.warning(errors[0])
      }
    })
  }

  const handleDelete = (note) => {
    swal
      .fire({
        title: 'Delete Note?',
        text: 'Are you sure you want to delete this note?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#e2001a',
      })
      .then(({ isConfirmed }) => {
        if (!isConfirmed) return
        deleteFn(note).then(({ success, errors }) => {
          if (!success) toast.warning(errors[0])
        })
      })
  }

  const handleUpdate = (note) => {
    const trimmed = editingContent.trim()
    setState({ editingId: null, editingContent: '' })
    if (!trimmed || trimmed === note.description) return
    updateFn({ ...note, checked: false, description: trimmed }).then(({ success, errors }) => {
      if (!success) toast.warning(errors[0])
    })
  }

  const noteComponent = (
    <Box flexDirection="column" padding={0} lineHeight="1.3">
      {filteredNotes.map((note, index) => (
        <div
          key={note.id}
          style={{ display: 'flex', alignItems: 'flex-start', borderBottom: (hideItemBorderBottom || (!editable && index === filteredNotes.length - 1)) ? 'none' : '1px solid #e8e8e8', padding: itemPadding }}
        >
          {checkable && (
            <Checkbox
              checked={!!note.checked}
              disabled={updating}
              onClick={() => updateFn({ ...note, checked: !note.checked })}
              style={{ marginRight: 10, flexShrink: 0 }}
            />
          )}
          {editable && editingId === note.id ? (
            <div style={{ flex: 1 }}>
              <textarea
                autoFocus
                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; el.scrollTop = el.scrollHeight; el.setSelectionRange(el.value.length, el.value.length) } }}
                value={editingContent}
                onChange={e => {
                  setState({ editingContent: e.target.value })
                  e.target.style.height = 'auto'
                  e.target.style.height = `${e.target.scrollHeight}px`
                }}
                onBlur={() => handleUpdate(note)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUpdate(note) }
                  if (e.key === 'Escape') setState({ editingId: null, editingContent: '' })
                }}
                maxLength={2000}
                style={{ resize: 'none', width: '100%', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 'small', overflow: 'hidden' }}
                rows={1}
              />
              <Text color="bodyFontLightColor" style={{ fontSize: 10, display: 'block', marginTop: 2 }}>Enter to update · Escape to cancel</Text>
            </div>
          ) : (
            <Text
              fontSize="small"
              color="bodyFontColor"
              style={{ flex: 1, cursor: editable ? 'pointer' : 'default', display: 'block' }}
              onClick={editable ? () => setState({ editingId: note.id, editingContent: note.description }) : undefined}
            >
              {note.description}
            </Text>
          )}
          {editable && (
            <button
              type="button"
              onClick={() => handleDelete(note)}
              disabled={deleting}
              style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, flexShrink: 0, alignSelf: 'start' }}
            >
              <Text fontSize="small" color="bodyFontLightColor">X</Text>
            </button>
          )}
        </div>
      ))}

      {editable && (
        <Box alignItems="center" padding={addPadding}>
          <textarea
            ref={addInputRef}
            placeholder="Add note..."
            value={newNoteContent}
            onChange={e => {
              setState({ newNoteContent: e.target.value })
              e.target.style.height = 'auto'
              e.target.style.height = `${e.target.scrollHeight}px`
            }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdd() } }}
            maxLength={2000}
            rows={1}
            style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', fontFamily: 'inherit', fontSize: 'small', overflow: 'hidden', lineHeight: 'inherit' }}
          />
          <Button
            buttonStyle="primaryCreate"
            disabled={!newNoteContent.trim()}
            loading={creating}
            onClick={handleAdd}
            size="medium"
            marginLeft="medium"
            width="auto"
          >
            Add
          </Button>
        </Box>
      )}
    </Box>
  )

  if (dashboardModule) {
    return (
      <DashboardModule
        title={title || 'Notes'}
        headerRight={showUpdatedStatus && (
          <MutationStatus isMutating={creating || updating || deleting} />
        )}
      >
        {noteComponent}
      </DashboardModule>
    )
  }

  return noteComponent
}

ChecklistItems.propTypes = {
  addPadding: PropTypes.string,
  checkable: PropTypes.bool,
  dashboardModule: PropTypes.bool,
  editable: PropTypes.bool,
  hideItemBorderBottom: PropTypes.bool,
  showUpdatedStatus: PropTypes.bool,
  itemPadding: PropTypes.string,
  serviceJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serviceJobChecklistItemTypeId: PropTypes.number,
  title: PropTypes.string,
}

export default ChecklistItems
